import {useRef, useEffect, useContext} from 'react';
import {Box, Typography, useMediaQuery, styled} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ResponsiveContext} from '../../pages/Symptoms/Questionnaire/finalResults';
import {faCircleCheck, faCircleXmark, faCircleMinus} from '@fortawesome/free-solid-svg-icons';
import {
  faFlask,
  faC,
  faVial,
  faT,
  faGaugeSimpleHigh,
  faBrain,
  faPerson,
  faHeartPulse,
  faStethoscope,
  faHeart,
  faDroplet,
  faWind,
  faCandyCane,
  faCompress,
  faWeightScale,
  faHeartCrack,
} from '@fortawesome/pro-light-svg-icons';
import {faLock} from '@fortawesome/pro-regular-svg-icons';
import {handleResultDisplayByMetric} from '../../pages/Symptoms/Questionnaire/finalResults';
import {faFace} from '../../assets/icons/';

/**
 * Array containing vital symbol icons(using the FontAwesomeInterface) by their respective values.
 * The index represents the vitalSymbolVal value. There is no index 0 and some of these icons are not being used
 * @type {(1:"faCompress" | 2: "faHeart" | 3: "faFace" | 4: "faHeartPulse" | 5:"faDroplet" | 6: "faWind" | 7: "faWeightScale" | 8: "faHeartCrack" | 9: "faBrain" | 10: "faPerson" | 11: "faFlask" | 12: "faC" | 13: "faVial" | 14: "faT" | 15: "faCandyCane" | 16: "faGaugeSimpleHigh" | 17: "faStethoscope")[]}
 */
const vitalSymbolValIcon = [
  undefined, //since there is no vitalSymbolVal with value 0, the first index is empty
  faCompress,
  faHeart,
  faFace,
  faHeartPulse,
  faDroplet,
  faWind,
  faWeightScale,
  faHeartCrack,
  faBrain,
  faPerson,
  faFlask,
  faC,
  faVial,
  faT,
  faCandyCane,
  faGaugeSimpleHigh,
  faStethoscope,
];

const BlurredText = styled(Typography)(() => ({
  filter: 'blur(7.5px)', // Set the blur effect here
}));

/**
 * Subcomponent of Metric Card
 * Handles te displaying in the case the current metric is a premium one and the user is not premium.
 * @param {Object} props - Component props
 * @param {string} props.title - name of the metric. This is just needed for displaying purposes
 * @param {string?} props.smallerText - Additional text that will go with the metric title(optional)
 *
 * @returns {React.Component} Rendered component
 */
const PremiumPaywallSubcomponent = ({title, smallerText}) => {
  const {mustChangeLayout, setMustChangeLayout} = useContext(ResponsiveContext);

  const is360pxOrBelow = useMediaQuery('(max-width:360px)');
  const metricTitleRef = useRef(undefined);
  const metricValueRef = useRef(undefined);

  useEffect(() => {
    if (
      metricTitleRef === undefined ||
      metricValueRef === undefined ||
      metricTitleRef.current === undefined ||
      metricValueRef.current === undefined
    )
      return;

    const metricTitleTop = metricTitleRef.current.getBoundingClientRect().top;
    const metricValueTop = metricValueRef.current.getBoundingClientRect().top;

    const topDiff = metricTitleTop - metricValueTop;
    if (topDiff < 0 || is360pxOrBelow) {
      setMustChangeLayout(true);
      return;
    }
  }, []);

  return (
    <>
      <Box
        key={`${title}-paywall`}
        ref={metricTitleRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(mustChangeLayout && {
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }),
        }}
      >
        <Typography variant="metricName" textAlign={mustChangeLayout ? 'center' : undefined}>
          {title}{' '}
          {smallerText === undefined ? (
            ''
          ) : (
            <Typography component={'span'} variant="metricLittleText">
              {smallerText}
            </Typography>
          )}
        </Typography>
        {/* this box will only be displayed if the layout is the same*/}
        <Box display={mustChangeLayout ? 'none' : 'flex'} alignItems="center">
          <Typography variant="metricRangeLabel" color="#3DA9F8" sx={{ml: 1}}>
            Upgrade to find out
          </Typography>
        </Box>
      </Box>
      <Box
        ref={metricValueRef}
        key={`${title}-paywall2`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          gap: 0.5,
          ...(mustChangeLayout && {
            mt: 1,
            width: '100%',
            rowGap: 0,
            justifyContent: 'center',
            flexWrap: 'wrap',
            alignContent: 'flex-end',
          }),
        }}
      >
        {/* this box will only be displayed if the layout changes*/}
        <Box
          display={mustChangeLayout ? 'flex' : 'none'}
          alignItems="center"
          justifyContent="center"
          sx={{flexGrow: 1, width: '100%', mb: 1}}
        >
          <Typography variant="metricRangeLabel" color="#3DA9F8" sx={{ml: 1}}>
            Upgrade to find out
          </Typography>
        </Box>
        <BlurredText variant={'metricNumber'} sx={{fontWeight: 'bold', textAlign: 'right'}}>
          3
        </BlurredText>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            minWidth: '55px',
          }}
        >
          <FontAwesomeIcon
            icon={faLock}
            size="xl"
            style={{
              color: '#319E98',
              opacity: '0.64',
            }}
          />
          {title !== 'Facial Skin Age' && title !== 'Mental Stress Index' ? (
            <Typography variant="metricUnit">percent</Typography>
          ) : null}
        </Box>
      </Box>
    </>
  );
};

/**
 * Metric Card
 * Rendered component to display the result of a given metric. Can be clickable to an event(opening a modal) and hidden depending on the user plan
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Name of the metric
 * @param {string?} props.smallerText - Additional text that will go with the metric title(optional)
 * @param {Number} props.result - Metric result
 * @param {Number} props.vitalSymbolVal - Index of vitalSymbolValIcon referring to which icon should the metric include besides the metric unit. In case is not defined then the unit will be displayed besides the result value(Optional)
 * @param {boolean} props.isPremium - Determines if the metric is only allowed for premium users so a paywall should be displayed instead of the actual metric
 * @param {string} props.vitalSignInitials - The metric unit used for that metric
 * @param {{
 *   colour: string,
 *   level: string
 * }} props.vitalStatus - Object with the label of the metrics range(level) and the colour for that leevel(colour)
 * @param {string} props.plan - Plan of the current user (for now there are only two: "basic" and "premium")
 * @param {(event: Event?) => void} props.handleClick - Callback executed when clicking the card
 *   The first parameter is the event object (Event).
 *   The second parameter is the result of the operation (string).
 *   The third parameter is the error code (number) - optional.
 *
 * @returns {React.Component} Rendered component
 */
export const MetricCard = ({
  title,
  smallerText,
  result,
  vitalSymbolVal,
  isPremium,
  vitalSignInitials,
  vitalStatus,
  plan,
  handleClick,
}) => {
  const {mustChangeLayout, setMustChangeLayout} = useContext(ResponsiveContext);
  const is360pxOrBelow = useMediaQuery('(max-width:360px)');

  const containerRef = useRef(undefined);
  const metricTitleRef = useRef(undefined);
  const metricValueRef = useRef(undefined);

  useEffect(() => {
    if (
      containerRef === undefined ||
      metricTitleRef === undefined ||
      metricValueRef === undefined ||
      containerRef.current === undefined ||
      metricTitleRef.current === undefined ||
      metricValueRef.current === undefined
    )
      return;

    const metricTitleTop = metricTitleRef.current.getBoundingClientRect().top;
    const metricValueTop = metricValueRef.current.getBoundingClientRect().top;

    const topDiff = metricTitleTop - metricValueTop;
    if (topDiff < 0 || is360pxOrBelow) {
      setMustChangeLayout(true);
      return;
    }
  }, []);
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        boxSizing: 'border-box',
        height: '100%',
        borderRadius: '20px',
        borderColor: '#D9D9D9',
        padding: 2,
        cursor: 'pointer',
        height: '100%',
        boxSizing: 'border-box',
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: 'rgba(217,217,217,0.10)',
        },
        transition: 'background-color 0.3s',
      }}
      onClick={handleClick}
    >
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          height: '100%',
          flexWrap: mustChangeLayout ? 'wrap' : undefined,
          justifyContent: 'space-between',
          py: 0.5,
          alignItems: 'top',
        }}
      >
        {plan !== 'premium' && isPremium ? (
          <PremiumPaywallSubcomponent title={title} smallerText={smallerText} />
        ) : (
          <>
            <Box
              key={`${title}`}
              ref={metricTitleRef}
              sx={{
                display: 'flex',
                flexShrink: 1,
                minWidth: 0,
                flexDirection: 'column',
                justifyContent: 'space-between',
                ...(mustChangeLayout && {
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }),
              }}
            >
              <Typography variant="metricName" textAlign={mustChangeLayout ? 'center' : undefined}>
                {title}{' '}
                {smallerText === undefined ? (
                  ''
                ) : (
                  <Typography component={'span'} variant="metricLittleText">
                    {smallerText}
                  </Typography>
                )}
              </Typography>
              {/* this box will only be displayed if the layout is the same*/}
              <Box display={mustChangeLayout ? 'none' : 'flex'} alignItems="center">
                <FontAwesomeIcon
                  icon={(() => {
                    if (vitalStatus.colour.match(/#fd5757|#ff8989/)) return faCircleXmark;

                    if (vitalStatus.colour.match(/#92e5b7|#62da9a/)) return faCircleCheck;
                    return faCircleMinus;
                  })()}
                  size="lg"
                  style={{color: vitalStatus.colour, marginRight: '8px'}}
                />

                <Typography
                  variant="metricRangeLabel"
                  color={vitalStatus.colour}
                  sx={{
                    ml: 0.5,
                  }}
                >
                  {vitalStatus.level}
                </Typography>
              </Box>
            </Box>
            <Box
              ref={metricValueRef}
              key={`${title}2`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                gap: 0.5,
                ...(mustChangeLayout && {
                  mt: 1,
                  width: '100%',
                  justifyContent: 'center',
                  rowGap: 0,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  alignContent: 'flex-end',
                }),
              }}
            >
              {/* this box will only be displayed if the layout changes*/}
              <Box
                display={mustChangeLayout ? 'flex' : 'none'}
                alignItems="center"
                justifyContent="center"
                sx={{flexGrow: 1, width: '100%', mb: 1}}
              >
                <FontAwesomeIcon
                  icon={(() => {
                    if (vitalStatus.colour.match(/#fd5757|#ff8989/)) return faCircleXmark;

                    if (vitalStatus.colour.match(/#92e5b7|#62da9a/)) return faCircleCheck;
                    return faCircleMinus;
                  })()}
                  size="lg"
                  style={{color: vitalStatus.colour, marginRight: '8px'}}
                />

                <Typography
                  variant="metricRangeLabel"
                  color={vitalStatus.colour}
                  sx={{
                    ml: 0.5,
                  }}
                >
                  {vitalStatus.level}
                </Typography>
              </Box>
              <Typography variant={'metricNumber'}>
                {handleResultDisplayByMetric(result, title)}
                {vitalSymbolVal === undefined ? vitalSignInitials : ''}
              </Typography>
              <Box
                sx={{
                  display: vitalSymbolVal === undefined ? 'none' : 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  minWidth: '55px', //important feature that maintains consistency in styles
                }}
              >
                {vitalSymbolVal !== undefined && (
                  <>
                    <FontAwesomeIcon
                      icon={vitalSymbolValIcon[vitalSymbolVal]}
                      size="xl"
                      style={{color: '#319E98', opacity: '0.34'}}
                    />
                    <Typography variant="metricUnit" sx={{mt: 1}}>
                      {vitalSignInitials}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
